import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Spin, Alert, Table, Input, Tag, Select, Tabs, Switch } from "antd";
import {
  SearchOutlined,
  ShopOutlined,
  RiseOutlined,
  BankOutlined,
  CrownOutlined,
} from "@ant-design/icons";
import customClientAxios from "../../customAxios";
import { PlatformAnalyticsTopCreators } from "../../utils/ApiEndpoints";
import {
  PageContainer,
  fmtRupees,
  fmtNum,
  StatsGrid,
  StatCard,
  StatLabel,
  StatValue,
  StatSub,
  IconBox,
  GRADIENTS,
} from "../paUtils";
import CreatorDetailDrawer from "./CreatorDetailDrawer";
import styled from "styled-components";

const PLAN_META = {
  free:     { label: "Free",     color: "#94a3b8" },
  growth:   { label: "Growth",   color: "#10b981" },
  pro:      { label: "Pro",      color: "#6366f1" },
  advanced: { label: "Advanced", color: "#a855f7" },
};
const PLAN_ORDER = ["free", "growth", "pro", "advanced"];

const fmtDate = (v) =>
  v
    ? new Date(v).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
    : "—";

const planEndOf = (r) => r.planPeriodEnd || r.trialExpiresAt || null;
const planStartOf = (r) => {
  if (r.planPeriodStart) return r.planPeriodStart;
  const end = planEndOf(r);
  if (!end) return null;
  const d = new Date(end);
  d.setMonth(d.getMonth() - 1);
  return d.toISOString();
};

const isSelling = (r) => (r.transactionCount || 0) > 0;
const isSetupNoSales = (r) => (r.offeringCount || 0) > 0 && (r.transactionCount || 0) === 0;
const isInactive = (r) => (r.offeringCount || 0) === 0 && (r.transactionCount || 0) === 0;

const TableCard = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
  border: 1px solid #e2e8f0;
  overflow: hidden;
`;

const TableHeader = styled.div`
  padding: 20px 24px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const TableTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
`;

const SegmentNote = styled.div`
  padding: 0 24px 16px;
  font-size: 12px;
  color: #64748b;
  line-height: 1.5;
`;

const FilterBar = styled.div`
  padding: 0 24px 16px;
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: center;
`;

const baseColumns = () => [
  {
    title: "#",
    key: "rank",
    width: 48,
    render: (_, __, i) => <span style={{ color: "#94a3b8", fontWeight: 600, fontSize: 13 }}>{i + 1}</span>,
  },
  {
    title: "Brand Page",
    key: "brand",
    render: (_, r) => (
      <div>
        <div style={{ fontWeight: 600, color: "#1e293b", fontSize: 13 }}>{r.brandPageName}</div>
        <div style={{ fontSize: 11, color: "#94a3b8" }}>/{r.slug}</div>
      </div>
    ),
  },
  {
    title: "Owner",
    key: "owner",
    render: (_, r) => (
      <div>
        <div style={{ fontWeight: 500, color: "#374151", fontSize: 13 }}>{r.ownerName}</div>
        <div style={{ fontSize: 11, color: "#94a3b8" }}>{r.ownerEmail}</div>
      </div>
    ),
  },
  {
    title: "Phone",
    key: "phone",
    width: 130,
    render: (_, r) => <span style={{ fontSize: 13 }}>{r.phone || "—"}</span>,
  },
];

const planColumns = () => [
  {
    title: "Plan",
    key: "plan",
    width: 110,
    render: (_, r) => {
      const p = r.plan || "free";
      const meta = PLAN_META[p];
      return (
        <div>
          <Tag color={meta?.color} style={{ fontSize: 11 }}>{meta?.label || p}</Tag>
          {r.trialExpiresAt && (
            <div style={{ fontSize: 10, color: "#92400e", marginTop: 2 }}>trial</div>
          )}
        </div>
      );
    },
  },
  {
    title: "Plan Start",
    key: "planStart",
    width: 120,
    sorter: (a, b) => new Date(planStartOf(a) || 0) - new Date(planStartOf(b) || 0),
    render: (_, r) => (
      <div>
        <div style={{ fontSize: 12 }}>{fmtDate(planStartOf(r))}</div>
        {r.activeSubSource === "trial" && (
          <div style={{ fontSize: 10, color: "#a855f7", fontWeight: 600, marginTop: 2 }}>
            TRIAL
          </div>
        )}
      </div>
    ),
  },
  {
    title: "Plan End",
    key: "planEnd",
    width: 120,
    sorter: (a, b) => new Date(planEndOf(a) || 0) - new Date(planEndOf(b) || 0),
    render: (_, r) => <span style={{ fontSize: 12 }}>{fmtDate(planEndOf(r))}</span>,
  },
];

const sellingColumns = [
  ...baseColumns(),
  {
    title: "GMV",
    dataIndex: "gmv",
    key: "gmv",
    defaultSortOrder: "descend",
    sorter: (a, b) => a.gmv - b.gmv,
    render: (v) => <span style={{ fontWeight: 700, color: "#10b981" }}>{fmtRupees(v)}</span>,
  },
  {
    title: "Platform Fee",
    dataIndex: "platformFee",
    key: "platformFee",
    sorter: (a, b) => a.platformFee - b.platformFee,
    render: (v) => <span style={{ color: "#6366f1", fontWeight: 600 }}>{fmtRupees(v)}</span>,
  },
  {
    title: "Creator Payout",
    dataIndex: "creatorPayout",
    key: "creatorPayout",
    render: (v) => fmtRupees(v),
  },
  {
    title: "Txns",
    dataIndex: "transactionCount",
    key: "transactionCount",
    sorter: (a, b) => a.transactionCount - b.transactionCount,
    render: (v) => <span style={{ fontWeight: 600 }}>{fmtNum(v)}</span>,
  },
  {
    title: "Customers",
    dataIndex: "uniqueCustomerCount",
    key: "uniqueCustomerCount",
    render: (v) => fmtNum(v),
  },
  {
    title: "Settled Payout",
    dataIndex: "settledPayoutAmount",
    key: "settledPayoutAmount",
    width: 140,
    sorter: (a, b) => (a.settledPayoutAmount || 0) - (b.settledPayoutAmount || 0),
    render: (v, r) => (
      <div>
        <div style={{ fontWeight: 600, color: "#0d9488" }}>{fmtRupees(v || 0)}</div>
        {r.settledPayoutCount > 0 && (
          <div style={{ fontSize: 10, color: "#94a3b8" }}>{r.settledPayoutCount} settlement{r.settledPayoutCount > 1 ? "s" : ""}</div>
        )}
      </div>
    ),
  },
  ...planColumns(),
  {
    title: "Plan Revenue",
    dataIndex: "planRevenue",
    key: "planRevenue",
    width: 130,
    sorter: (a, b) => (a.planRevenue || 0) - (b.planRevenue || 0),
    render: (v, r) => (
      <div>
        <div style={{ fontWeight: 600, color: "#a855f7" }}>{fmtRupees(v || 0)}</div>
        {r.planTxnCount > 0 && (
          <div style={{ fontSize: 10, color: "#94a3b8" }}>{r.planTxnCount} txn{r.planTxnCount > 1 ? "s" : ""}</div>
        )}
      </div>
    ),
  },
];

const setupColumns = [
  ...baseColumns(),
  {
    title: "Offerings",
    dataIndex: "offeringCount",
    key: "offeringCount",
    width: 110,
    defaultSortOrder: "descend",
    sorter: (a, b) => (a.offeringCount || 0) - (b.offeringCount || 0),
    render: (v) => <span style={{ fontWeight: 600 }}>{fmtNum(v)}</span>,
  },
  {
    title: "Upcoming Event",
    dataIndex: "hasUpcomingPublishedEvent",
    key: "hasUpcomingPublishedEvent",
    width: 150,
    render: (v) =>
      v ? (
        <Tag color="#10b981" style={{ fontSize: 11 }}>Live & upcoming</Tag>
      ) : (
        <span style={{ color: "#94a3b8", fontSize: 12 }}>—</span>
      ),
  },
  ...planColumns(),
];

const inactiveColumns = [
  ...baseColumns(),
  ...planColumns(),
];

const KpiCard = ({ label, value, sub, gradient, icon }) => (
  <StatCard>
    <IconBox $bg={gradient}>{icon}</IconBox>
    <StatLabel>{label}</StatLabel>
    <StatValue>{value}</StatValue>
    {sub && <StatSub>{sub}</StatSub>}
  </StatCard>
);

function BrandPagesTab({ startDate, endDate }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [planFilter, setPlanFilter] = useState([]);
  const [segment, setSegment] = useState("selling");
  const [onlyUpcomingEvent, setOnlyUpcomingEvent] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedBpId, setSelectedBpId] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await customClientAxios().get(PlatformAnalyticsTopCreators, {
        params: { startDate, endDate, sortBy: "gmv", limit: 2000 },
      });
      setData(res.data.data?.creators || []);
    } catch (e) {
      setError(e?.response?.data?.error || "Failed to load brand pages");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // KPIs are computed from the full list (all brand pages), not the filtered
  // search/segment view, so the headline numbers don't move when the user
  // narrows the table.
  const kpis = useMemo(() => {
    const total = data.length;
    let selling = 0;
    let withPayout = 0;
    let settledTotalPaise = 0;
    let paidPlan = 0;
    let setupNoSales = 0;
    for (const r of data) {
      if (isSelling(r)) selling += 1;
      if ((r.settledPayoutCount || 0) > 0) {
        withPayout += 1;
        settledTotalPaise += r.settledPayoutAmount || 0;
      }
      if (r.plan && r.plan !== "free") paidPlan += 1;
      if (isSetupNoSales(r)) setupNoSales += 1;
    }
    return { total, selling, withPayout, settledTotalPaise, paidPlan, setupNoSales };
  }, [data]);

  const segmentData = useMemo(() => {
    if (segment === "selling") return data.filter(isSelling);
    if (segment === "setup") {
      const base = data.filter(isSetupNoSales);
      return onlyUpcomingEvent ? base.filter((r) => r.hasUpcomingPublishedEvent) : base;
    }
    return data.filter(isInactive);
  }, [data, segment, onlyUpcomingEvent]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return segmentData.filter((c) => {
      if (planFilter.length && !planFilter.includes(c.plan || "free")) return false;
      if (!s) return true;
      return (
        c.brandPageName?.toLowerCase().includes(s) ||
        c.ownerName?.toLowerCase().includes(s) ||
        c.ownerEmail?.toLowerCase().includes(s) ||
        c.phone?.toLowerCase().includes(s) ||
        c.slug?.toLowerCase().includes(s)
      );
    });
  }, [segmentData, search, planFilter]);

  const columns =
    segment === "selling" ? sellingColumns :
    segment === "setup"   ? setupColumns   :
                            inactiveColumns;

  const segmentNote =
    segment === "selling"
      ? "Brand pages with at least one successful sale in the selected date range. GMV / fee / payout columns also respect the range."
      : segment === "setup"
      ? "Brand pages that have built at least one offering but haven't sold yet in this range. Use the toggle to focus on pages that still have a live, upcoming event."
      : "Brand pages with no offerings created and no sales — signed up but never set up the storefront.";

  const tabItems = [
    { key: "selling", label: `Selling (${kpis.selling})` },
    { key: "setup",   label: `Set up, no sales yet (${kpis.setupNoSales})` },
    { key: "inactive", label: `Inactive (${kpis.total - kpis.selling - kpis.setupNoSales})` },
  ];

  return (
    <PageContainer>
      {error && <Alert type="error" message={error} style={{ marginBottom: 20 }} />}

      <StatsGrid>
        <KpiCard
          label="Active Brand Pages"
          value={fmtNum(kpis.total)}
          sub={`${fmtNum(kpis.paidPlan)} on a paid plan`}
          gradient={GRADIENTS.indigo}
          icon={<ShopOutlined />}
        />
        <KpiCard
          label="Selling"
          value={fmtNum(kpis.selling)}
          sub={kpis.total ? `${Math.round((kpis.selling / kpis.total) * 100)}% of brand pages` : "—"}
          gradient={GRADIENTS.green}
          icon={<RiseOutlined />}
        />
        <KpiCard
          label="Received Payout (lifetime)"
          value={fmtNum(kpis.withPayout)}
          sub={`${fmtRupees(kpis.settledTotalPaise)} settled to creators`}
          gradient={GRADIENTS.teal}
          icon={<BankOutlined />}
        />
        <KpiCard
          label="Paid Plan Subscribers"
          value={fmtNum(kpis.paidPlan)}
          sub={kpis.total ? `${Math.round((kpis.paidPlan / kpis.total) * 100)}% of brand pages` : "—"}
          gradient={GRADIENTS.purple}
          icon={<CrownOutlined />}
        />
      </StatsGrid>

      <TableCard>
        <TableHeader>
          <TableTitle>Brand Pages ({filtered.length})</TableTitle>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Select
              mode="multiple"
              allowClear
              placeholder="Filter by plan"
              value={planFilter}
              onChange={setPlanFilter}
              style={{ minWidth: 200 }}
              options={PLAN_ORDER.map((p) => ({
                value: p,
                label: (
                  <span>
                    <Tag color={PLAN_META[p].color} style={{ marginRight: 6 }}>
                      {PLAN_META[p].label}
                    </Tag>
                  </span>
                ),
              }))}
            />
            <Input
              placeholder="Search by name, email, phone or slug..."
              prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: 280 }}
              allowClear
            />
          </div>
        </TableHeader>
        <Tabs
          activeKey={segment}
          onChange={setSegment}
          items={tabItems}
          style={{ padding: "0 24px" }}
        />
        <SegmentNote>{segmentNote}</SegmentNote>
        {segment === "setup" && (
          <FilterBar>
            <Switch
              checked={onlyUpcomingEvent}
              onChange={setOnlyUpcomingEvent}
              size="small"
            />
            <span style={{ fontSize: 13, color: "#374151" }}>
              Only brand pages with ≥1 live/published upcoming event
            </span>
          </FilterBar>
        )}
        <Spin spinning={loading}>
          <Table
            dataSource={filtered}
            columns={columns}
            rowKey="brandPageId"
            size="middle"
            pagination={{ pageSize: 20, showSizeChanger: true, pageSizeOptions: [20, 50, 100] }}
            onRow={(record) => ({
              onClick: () => { setSelectedBpId(record.brandPageId); setDrawerOpen(true); },
              style: { cursor: "pointer" },
            })}
            scroll={{ x: segment === "selling" ? 1500 : 1100 }}
          />
        </Spin>
      </TableCard>

      <CreatorDetailDrawer
        brandPageId={selectedBpId}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      />
    </PageContainer>
  );
}

export default BrandPagesTab;
